// src/components/settings/impact-scorecard-tab.jsx
// IMPACT Scorecard — a Settings tab that auto-calculates the user's monthly
// coaching numbers (Me or My Team), merges in saved goals, and generates the
// filled PDF. Visibility is gated upstream in main-settings.jsx; we also guard
// here so the component is safe to render directly.
import { useEffect, useMemo, useState } from "react";
import { Download, Loader2, Lock, Save } from "lucide-react";
import { toast } from "react-toastify";
import moment from "moment";
import { useStateValue } from "../../context/StateProvider";
import { isImpactScorecardEnabled } from "../../utils/featureFlags";
import {
  computeScorecardMetrics,
  buildScorecardFields,
} from "../../utils/impactScorecard";
import { loadScorecard, saveScorecard } from "../../utils/impactScorecardStore";
import { fillScorecardPdf, downloadBytes } from "../../utils/impactScorecardPdf";

const fmtMoney = (n) =>
  "$" + Number(n || 0).toLocaleString("en-US", { maximumFractionDigits: 0 });

const inputCls =
  "w-full rounded-lg border border-gray-300 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-indigo-200";
const labelCls = "text-[13px] text-slate-500 mb-1 block";

export default function ImpactScorecardTab() {
  const [{ user, deals }] = useStateValue();

  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [generating, setGenerating] = useState(false);
  const [scope, setScope] = useState("me");

  const [goals, setGoals] = useState({
    why: "",
    yearGoals: ["", "", "", ""],
    personalGoals: ["", ""],
    quarterGoals: ["", "", ""],
    accountability: "",
    contractsRequired: "",
    contractsRequiredNextMonth: "",
  });
  const [savings, setSavings] = useState("");
  const [coachingCallDate, setCoachingCallDate] = useState("");
  // Manual overrides of the auto-calculated numbers. Only the keys the user
  // edits are present; the rest fall through to the live metric value.
  const [overrides, setOverrides] = useState({});

  const metrics = useMemo(() => {
    const asOf = new Date();
    return {
      me: computeScorecardMetrics(deals, { agentId: user?.id, asOf }),
      team: computeScorecardMetrics(deals, { agentId: undefined, asOf }),
    };
  }, [deals, user?.id]);

  // Displayed numbers come straight from the selected scope's live metrics,
  // except any field the user has manually overridden. Because this is derived
  // (not state), a background deals refresh updates the auto fields without ever
  // clobbering an override. Overrides are cleared when the scope is switched.
  const selected = scope === "me" ? metrics.me : metrics.team;
  const numbers = {
    units: overrides.units ?? String(selected.unitsLastMonth),
    volume: overrides.volume ?? fmtMoney(selected.volumeLastMonth),
    grossIncome: overrides.grossIncome ?? fmtMoney(selected.grossIncomeLastMonth),
    contractsMtdActual:
      overrides.contractsMtdActual ?? String(selected.contractsThisMonth),
    contractsNextActual:
      overrides.contractsNextActual ?? String(selected.contractsNextMonth),
  };

  useEffect(() => {
    if (!user?.id) return undefined;
    let cancelled = false;
    loadScorecard(user.id).then((s) => {
      if (cancelled) return;
      setGoals({
        why: s.why || "",
        yearGoals: (s.yearGoals?.length ? s.yearGoals : ["", "", "", ""]).slice(0, 4),
        personalGoals: (s.personalGoals?.length ? s.personalGoals : ["", ""]).slice(0, 2),
        // The 2026 sheet has 3 quarter-goal slots (goal #3 wraps to two lines).
        quarterGoals: (s.quarterGoals?.length ? s.quarterGoals : ["", "", ""]).slice(0, 3),
        accountability: s.accountability || "",
        contractsRequired: s.contractsRequired || "",
        contractsRequiredNextMonth: s.contractsRequiredNextMonth || "",
      });
      if (s.lastScope === "me" || s.lastScope === "team") setScope(s.lastScope);
      setLoading(false);
    });
    return () => {
      cancelled = true;
    };
  }, [user?.id]);

  const setGoalField = (key, value) => setGoals((g) => ({ ...g, [key]: value }));
  const setListItem = (key, idx, value) =>
    setGoals((g) => {
      const next = [...g[key]];
      next[idx] = value;
      return { ...g, [key]: next };
    });
  const setNum = (key, value) => setOverrides((o) => ({ ...o, [key]: value }));

  const handleSave = async () => {
    setSaving(true);
    try {
      await saveScorecard(user.id, { ...goals, lastScope: scope });
      toast.success("Scorecard goals saved");
    } catch (err) {
      toast.error("Couldn't save — try again");
    } finally {
      setSaving(false);
    }
  };

  const handleGenerate = async () => {
    setGenerating(true);
    try {
      const name =
        [user?.firstname, user?.lastname].filter(Boolean).join(" ") ||
        user?.name ||
        user?.email ||
        "";
      const fields = buildScorecardFields({
        name,
        coachingCallDate,
        units: numbers.units,
        volume: numbers.volume,
        grossIncome: numbers.grossIncome,
        savings,
        contractsMtdActual: numbers.contractsMtdActual,
        contractsMtdRequired: goals.contractsRequired,
        contractsNextActual: numbers.contractsNextActual,
        contractsNextRequired: goals.contractsRequiredNextMonth,
        why: goals.why,
        yearGoals: goals.yearGoals,
        personalGoals: goals.personalGoals,
        quarterGoals: goals.quarterGoals,
        accountability: goals.accountability,
      });
      const bytes = await fillScorecardPdf(fields);
      downloadBytes(bytes, `IMPACT Scorecard - ${moment().format("MMMM YYYY")}.pdf`);
      toast.success("Scorecard PDF generated");
    } catch (err) {
      console.error(err);
      toast.error("Couldn't generate the PDF — check the template");
    } finally {
      setGenerating(false);
    }
  };

  if (!isImpactScorecardEnabled({ user })) return null;
  if (loading) {
    return (
      <div className="flex items-center gap-2 text-slate-500 p-6">
        <Loader2 className="w-4 h-4 animate-spin" /> Loading…
      </div>
    );
  }

  return (
    <div className="max-w-3xl space-y-5">
      <div className="flex items-center justify-between gap-3 flex-wrap">
        <h2 className="text-lg sm:text-xl font-semibold text-gray-800">
          IMPACT Scorecard
        </h2>
        <span className="inline-flex items-center gap-1.5 rounded-md bg-slate-100 text-slate-500 text-[12px] px-2.5 py-1">
          <Lock className="w-3.5 h-3.5" /> Super Admin — Only You
        </span>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
        <div>
          <label className={labelCls}>Coaching Call Date</label>
          <input
            type="date"
            className={inputCls}
            value={coachingCallDate}
            onChange={(e) => setCoachingCallDate(e.target.value)}
          />
        </div>
        <div>
          <label className={labelCls}>Production Scope</label>
          <div className="inline-flex rounded-lg border border-gray-300 overflow-hidden">
            {["me", "team"].map((opt) => (
              <button
                key={opt}
                type="button"
                onClick={() => {
                  setScope(opt);
                  setOverrides({});
                }}
                className={`px-4 py-2 text-sm ${
                  scope === opt
                    ? "bg-indigo-50 text-indigo-700"
                    : "text-slate-600 hover:bg-slate-100"
                }`}
              >
                {opt === "me" ? "Me" : "My Team"}
              </button>
            ))}
          </div>
        </div>
      </div>

      <div className="rounded-xl border border-gray-200 bg-white p-4">
        <div className="flex items-center justify-between mb-3">
          <p className="font-semibold text-slate-800">Your Scorecard Numbers</p>
          <span className="text-[12px] text-slate-400">
            Auto-calculated · editable before you generate
          </span>
        </div>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <div>
            <label className={labelCls}>Units Last Month</label>
            <input
              className={inputCls}
              value={numbers.units}
              onChange={(e) => setNum("units", e.target.value)}
            />
          </div>
          <div>
            <label className={labelCls}>Volume Last Month</label>
            <input
              className={inputCls}
              value={numbers.volume}
              onChange={(e) => setNum("volume", e.target.value)}
            />
          </div>
          <div>
            <label className={labelCls}>Gross Income Last Month</label>
            <input
              className={inputCls}
              value={numbers.grossIncome}
              onChange={(e) => setNum("grossIncome", e.target.value)}
            />
          </div>
          <div>
            <label className={labelCls}>Savings Last Month (2nd call)</label>
            <input
              className={inputCls}
              placeholder="—"
              value={savings}
              onChange={(e) => setSavings(e.target.value)}
            />
          </div>
          <div>
            <label className={labelCls}>Contracts Closing This Month — Actual (MTD)</label>
            <input
              className={inputCls}
              value={numbers.contractsMtdActual}
              onChange={(e) => setNum("contractsMtdActual", e.target.value)}
            />
          </div>
          <div>
            <label className={labelCls}>Contracts This Month — Required (Target)</label>
            <input
              className={inputCls}
              value={goals.contractsRequired}
              onChange={(e) => setGoalField("contractsRequired", e.target.value)}
            />
          </div>
          <div>
            <label className={labelCls}>Contracts Closing Next Month — Actual</label>
            <input
              className={inputCls}
              value={numbers.contractsNextActual}
              onChange={(e) => setNum("contractsNextActual", e.target.value)}
            />
          </div>
          <div>
            <label className={labelCls}>Contracts Next Month — Required (Target)</label>
            <input
              className={inputCls}
              value={goals.contractsRequiredNextMonth}
              onChange={(e) => setGoalField("contractsRequiredNextMonth", e.target.value)}
            />
          </div>
        </div>
      </div>

      <div className="rounded-xl border border-gray-200 bg-white p-4 space-y-4">
        <p className="font-semibold text-slate-800">
          Goals &amp; Why{" "}
          <span className="font-normal text-[12px] text-slate-400">
            — saved once, carries over each month
          </span>
        </p>

        <div>
          <label className={labelCls}>My Why</label>
          <input
            className={inputCls}
            value={goals.why}
            onChange={(e) => setGoalField("why", e.target.value)}
          />
        </div>

        <div>
          <label className={labelCls}>Current Quarter Goals</label>
          <div className="space-y-2">
            {goals.quarterGoals.map((g, i) => (
              <input
                key={i}
                className={inputCls}
                placeholder={`Quarter Goal ${i + 1}`}
                value={g}
                onChange={(e) => setListItem("quarterGoals", i, e.target.value)}
              />
            ))}
          </div>
        </div>

        <div>
          <label className={labelCls}>Top 4 Goals for the Year</label>
          <div className="space-y-2">
            {goals.yearGoals.map((g, i) => (
              <input
                key={i}
                className={inputCls}
                placeholder={`Goal ${i + 1}`}
                value={g}
                onChange={(e) => setListItem("yearGoals", i, e.target.value)}
              />
            ))}
          </div>
        </div>

        <div>
          <label className={labelCls}>Top 2 Personal Goals for the Year</label>
          <div className="space-y-2">
            {goals.personalGoals.map((g, i) => (
              <input
                key={i}
                className={inputCls}
                placeholder={`Personal Goal ${i + 1}`}
                value={g}
                onChange={(e) => setListItem("personalGoals", i, e.target.value)}
              />
            ))}
          </div>
        </div>

        <div>
          <label className={labelCls}>Accountability Choice</label>
          <input
            className={inputCls}
            placeholder="e.g. ice bath, charity donation, plank…"
            value={goals.accountability}
            onChange={(e) => setGoalField("accountability", e.target.value)}
          />
        </div>
      </div>

      <div className="flex items-center justify-between gap-3 flex-wrap">
        <span className="text-[13px] text-slate-500">
          Generating for:{" "}
          <strong className="font-medium text-indigo-700">
            {scope === "me" ? "Me" : "My Team"}
          </strong>
        </span>
        <div className="flex items-center gap-2">
          <button
            type="button"
            onClick={handleSave}
            disabled={saving}
            className="inline-flex items-center gap-2 rounded-lg border border-gray-300 bg-white px-4 py-2 text-sm text-slate-700 hover:bg-slate-50 disabled:opacity-60"
          >
            {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />}
            Save Goals
          </button>
          <button
            type="button"
            onClick={handleGenerate}
            disabled={generating}
            className="inline-flex items-center gap-2 rounded-lg bg-indigo-600 px-4 py-2 text-sm text-white hover:bg-indigo-700 disabled:opacity-60"
          >
            {generating ? <Loader2 className="w-4 h-4 animate-spin" /> : <Download className="w-4 h-4" />}
            Generate Scorecard PDF
          </button>
        </div>
      </div>
    </div>
  );
}
